import React, {
  ReactElement,
  useState,
  useRef,
  useCallback,
  useEffect,
} from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  Button,
} from '@mui/material'
import { Close } from '@mui/icons-material'
import MuxPlayer from '@mux/mux-player-react'
import { TranscriptEditor } from 'src/v3/components/transcript-editor/transcript-editor'
import { useAppSelector, useAppDispatch } from 'src/store/hooks'
import { selectUserID } from 'src/v3/settings/settings.slice'
import { useVttSubtitles } from 'src/v3/utilities/hooks/use-vtt-subtitles'
import {
  fetchVideos,
  selectVideoByID,
  selectVideosStatus,
} from 'src/v3/create/create.slice'

interface TranscriptEditorModalProps {
  open: boolean
  onClose: () => void
  videoId: string
}

export function TranscriptEditorModal({
  open,
  onClose,
  videoId,
}: TranscriptEditorModalProps): ReactElement {
  let dispatch = useAppDispatch()
  let userID = useAppSelector(selectUserID)
  let videosStatus = useAppSelector(selectVideosStatus)
  let video = useAppSelector(selectVideoByID(videoId))

  let [currentTime, setCurrentTime] = useState(0)
  let playerRef = useRef(null)

  // Load VTT subtitles if available
  let { vttUrl } = useVttSubtitles(videoId)

  useEffect(() => {
    if (videosStatus === 'uninitialized') {
      dispatch(fetchVideos())
    }
  }, [videosStatus, dispatch])

  // Function to seek to a specific time in the video
  let handleSeek = useCallback(
    (time: number) => {
      if (playerRef.current) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore - MuxPlayer API might not be fully typed
        // eslint-disable-next-line fp/no-mutation
        playerRef.current.currentTime = time
      }
    },
    [playerRef],
  )

  // Handle time updates from the player
  let handleTimeUpdate = (event: any) => {
    let player = event.target
    setCurrentTime(player.currentTime)
  }

  if (!video) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        slotProps={{
          paper: {
            sx: {
              width: '80vw',
              height: '80vh',
              maxWidth: 'none',
              maxHeight: 'none',
            },
          },
        }}
      >
        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading video...</Typography>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: '80vw',
            height: '80vh',
            maxWidth: 'none',
            maxHeight: 'none',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography variant="h6">Edit Transcript: {video.name}</Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Video Player */}
        <Paper elevation={3} sx={{ mb: 3, flexShrink: 0 }}>
          <Box sx={{ height: '300px', maxHeight: '300px' }}>
            <MuxPlayer
              ref={playerRef}
              title={video.name}
              src={`${video?.fileName.replace('mp4', 'm3u8')}`}
              accentColor="#f9ea4f"
              primaryColor="#6694E9"
              backwardSeekOffset={10}
              forwardSeekOffset={10}
              metadata={{
                video_id: video.id,
                video_title: video.name,
                viewer_user_id: userID,
              }}
              onTimeUpdate={handleTimeUpdate}
              defaultHiddenCaptions={!vttUrl}
              streamType="on-demand"
              style={{ height: '100%', width: '100%', maxHeight: '300px' }}
            >
              {vttUrl && (
                <track
                  src={vttUrl}
                  kind="subtitles"
                  label="English"
                  srcLang="en"
                  default
                />
              )}
            </MuxPlayer>
          </Box>
        </Paper>

        {/* Transcript Editor */}
        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0, // Allow shrinking
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <TranscriptEditor
            currentTime={currentTime}
            onSeek={handleSeek}
            videoId={videoId}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TranscriptEditorModal
